"use client";

import { Calendar, ArrowRight } from "lucide-react";
import Link from "next/link";

const EventCard = ({ event }) => {
    const getEventStatus = (startDate, endDate) => {
        if (!startDate) return { text: "UPCOMING", color: "bg-primary" };

        const eventStartDate = new Date(startDate);
        const eventEndDate = endDate ? new Date(endDate) : eventStartDate;
        const currentDate = new Date();

        // Remove time component for date comparison
        const startDateOnly = new Date(
            eventStartDate.getFullYear(),
            eventStartDate.getMonth(),
            eventStartDate.getDate()
        );
        const endDateOnly = new Date(
            eventEndDate.getFullYear(),
            eventEndDate.getMonth(),
            eventEndDate.getDate()
        );
        const currentDateOnly = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate()
        );

        if (currentDateOnly < startDateOnly) {
            return { text: "UPCOMING", color: "bg-primary" };
        } else if (
            currentDateOnly >= startDateOnly &&
            currentDateOnly <= endDateOnly
        ) {
            return { text: "ONGOING", color: "bg-logoGreen" };
        } else {
            return { text: "EXPIRED", color: "bg-logoRed" };
        }
    };

    const formatEventDate = (startDate, endDate) => {
        // Check multiple possible date field formats
        const getValidDate = (dateValue) => {
            if (!dateValue) return null;
            const date = new Date(dateValue);
            return isNaN(date.getTime()) ? null : date;
        };

        const start = getValidDate(startDate);
        if (!start) return "TBD";

        const startFormatted = start.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });

        if (!endDate) {
            return startFormatted;
        }
        if (startDate === endDate) {
            return startFormatted;
        }

        const end = getValidDate(endDate);
        if (!end) return startFormatted;

        const endFormatted = end.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });

        return `${startFormatted} - ${endFormatted}`;
    };

    return (
        <Link href={`/event/${event.slug}`}>
            <div
                className="group cursor-pointer rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1"
                style={{
                    backgroundColor: 'var(--color-card)',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'var(--color-border)'
                }}
            >
            {/* Feature Image */}
            {event.featuredImage && (
                <div className="w-full h-32 sm:h-40 md:h-48 overflow-hidden relative" style={{ backgroundColor: 'var(--color-muted)' }}>
                    <img
                        src={event.featuredImage}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                            e.target.style.display = "none";
                        }}
                    />
                    {/* Event Status Badge */}
                    <div
                        className={`absolute top-1 sm:top-2 md:top-3 left-1 sm:left-2 md:left-3 px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 md:py-1.5 rounded-full text-xs font-semibold text-primary-foreground ${getEventStatus(event.startDate, event.endDate).color}`}
                    >
                        {getEventStatus(event.startDate, event.endDate).text}
                    </div>
                </div>
            )}

            <div className="p-2 space-y-2">
                <h3 className="text-sm md:text-base font-bold transition-colors line-clamp-2 group-hover:text-primary"
                    style={{ color: 'var(--color-heading)' }}>
                    {event.title}
                </h3>

                {/* Description/Excerpt */}
                {event.excerpt && (
                    <p className="text-xs line-clamp-2" style={{ color: 'var(--color-paragraph)' }}>
                        {event.excerpt}
                    </p>
                )}

                {/* Date */}
                <div className="flex items-center text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                    <Calendar className="w-3 h-3 mr-1" style={{ color: 'var(--color-primary)' }} />
                    <span>{formatEventDate(
                        event.startDate || event.eventDate || event.publishDate || event.publishedAt || event.createdAt,
                        event.endDate
                    )}</span>
                </div>

                {/* View More Button */}
                <div className="flex items-center pt-2"
                    style={{ borderTopWidth: '1px', borderTopColor: 'var(--color-border)' }}>
                    <span className="text-xs font-semibold group-hover:text-primary transition-colors"
                        style={{ color: 'var(--color-link)' }}>
                        View Details
                    </span>
                    <ArrowRight className="w-3 h-3 ml-2 group-hover:translate-x-1 transition-transform duration-200"
                        style={{ color: 'var(--color-link)' }} />
                </div>
            </div>
        </div>
        </Link>
    );
};

export default EventCard;
